import { useEffect, useState } from "react";
import { ConfirmModal } from "./ConfirmModal";
import { showToast } from "./toast";

interface EnvVarRow {
  name: string;
  description: string | null;
  secret: boolean;
  updatedAt: string;
  updatedBy: string;
}

/**
 * Gerenciador de variáveis de ambiente (PATs, chaves de API...): LISTA ÚNICA
 * da ORGANIZAÇÃO - as mesmas variáveis aparecem em todas as telas que rendam
 * este componente (Instruções, Repositórios...) e valem em toda conversa de
 * qualquer workspace. Cifradas em repouso e injetadas no env do agente como
 * $NOME; secreta = write-only (valor nunca volta pra UI).
 */
export function EnvVarsManager({
  qs = "",
  startOpen = false,
}: {
  /** Query extra (ex.: ?orgId= do sysadmin). */
  qs?: string;
  startOpen?: boolean;
}) {
  const base = "/api/admin/org/env";
  const [open, setOpen] = useState(startOpen);
  const [items, setItems] = useState<EnvVarRow[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // formulário (criar/atualizar)
  const [name, setName] = useState("");
  const [value, setValue] = useState("");
  const [showValue, setShowValue] = useState(false);
  const [description, setDescription] = useState("");
  const [secret, setSecret] = useState(true);
  const [toDelete, setToDelete] = useState<string | null>(null);
  const [revealed, setRevealed] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!open || loaded) return;
    fetch(`${base}${qs}`)
      .then((r) => (r.ok ? r.json() : { items: [] }))
      .then((j: { items?: EnvVarRow[] }) => setItems(j.items ?? []))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, [open, loaded, base, qs]);

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await fetch(`${base}${qs}`, {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ name, value: value || undefined, description, secret }),
      });
      const j = (await r.json().catch(() => ({}))) as { error?: string; items?: EnvVarRow[] };
      if (!r.ok) {
        setError(j.error ?? "falha ao salvar a variável");
        return;
      }
      setItems(j.items ?? []);
      setName("");
      setValue("");
      setDescription("");
      setSecret(true);
      setShowValue(false);
      showToast("Variável salva - vale a partir da próxima resposta do agente.", "success");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (n: string) => {
    setBusy(true);
    try {
      const r = await fetch(`${base}/${encodeURIComponent(n)}${qs}`, { method: "DELETE" });
      const j = (await r.json().catch(() => ({}))) as { error?: string; items?: EnvVarRow[] };
      if (!r.ok) {
        showToast(j.error ?? "falha ao remover", "error");
        return;
      }
      setItems(j.items ?? []);
      setRevealed((prev) => {
        const next = { ...prev };
        delete next[n];
        return next;
      });
    } finally {
      setBusy(false);
    }
  };

  const reveal = async (n: string) => {
    if (revealed[n] !== undefined) {
      setRevealed((prev) => {
        const next = { ...prev };
        delete next[n];
        return next;
      });
      return;
    }
    const r = await fetch(`${base}/${encodeURIComponent(n)}/reveal${qs}`);
    const j = (await r.json().catch(() => ({}))) as { value?: string; error?: string };
    if (!r.ok || j.value === undefined) {
      showToast(j.error ?? "não foi possível revelar", "error");
      return;
    }
    setRevealed((prev) => ({ ...prev, [n]: j.value! }));
  };

  const editVar = (v: EnvVarRow) => {
    setName(v.name);
    setValue("");
    setDescription(v.description ?? "");
    setSecret(v.secret);
  };

  return (
    <div className="env-vars">
      <button type="button" className="env-vars-toggle" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        <svg width="11" height="11" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d={open ? "M4 6l4 4 4-4" : "M6 4l4 4-4 4"} />
        </svg>
        Variáveis de ambiente da organização{loaded ? ` (${items.length})` : ""}
      </button>
      {open && (
        <div className="env-vars-body">
          <p className="admin-user-meta" style={{ margin: "4px 0 10px" }}>
            Lista ÚNICA da organização: as chaves (ex.: PAT do Azure DevOps) valem em toda conversa de
            qualquer workspace, entram no ambiente do agente como <code>$NOME</code> e as instruções podem
            referenciá-las pelo nome - ele as usa proativamente pra consultar tasks, PRs e boards.{" "}
            <strong>Secreta</strong> = o valor nunca mais é exibido (só substituído).
          </p>
          {items.length > 0 && (
            <div style={{ overflowX: "auto", marginBottom: 10 }}>
              <table className="admin-table" style={{ width: "100%", fontSize: 13 }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: "left" }}>Nome</th>
                    <th style={{ textAlign: "left" }}>Descrição</th>
                    <th style={{ textAlign: "left" }}>Valor</th>
                    <th style={{ textAlign: "left" }}>Atualizada</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {items.map((v) => (
                    <tr key={v.name}>
                      <td><code>{v.name}</code></td>
                      <td>{v.description ?? <span className="admin-user-meta">—</span>}</td>
                      <td>
                        {v.secret ? (
                          <span className="badge warn" title="Write-only: o valor nunca é exibido">secreta</span>
                        ) : revealed[v.name] !== undefined ? (
                          <code style={{ wordBreak: "break-all" }}>{revealed[v.name]}</code>
                        ) : (
                          <span className="admin-user-meta">••••••••</span>
                        )}
                        {!v.secret && (
                          <button type="button" className="btn-page" style={{ marginLeft: 6 }} onClick={() => void reveal(v.name)}>
                            {revealed[v.name] !== undefined ? "Ocultar" : "Revelar"}
                          </button>
                        )}
                      </td>
                      <td className="admin-user-meta">
                        {new Date(v.updatedAt).toLocaleDateString("pt-BR", { timeZone: "America/Sao_Paulo" })} · {v.updatedBy}
                      </td>
                      <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                        <button type="button" className="btn-approve" disabled={busy} onClick={() => editVar(v)} title="Carregar no formulário pra substituir o valor">
                          Editar
                        </button>{" "}
                        <button type="button" className="btn-reject" disabled={busy} onClick={() => setToDelete(v.name)}>
                          Remover
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {error && <div className="auth-error" style={{ marginBottom: 8 }}>{error}</div>}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
            <label className="field" style={{ width: 180 }}>
              <span>Nome (MAIÚSCULAS_E_UNDERSCORE)</span>
              <input
                type="text"
                value={name}
                placeholder="AZURE_DEVOPS_PAT"
                onChange={(e) => setName(e.target.value.toUpperCase())}
                spellCheck={false}
              />
            </label>
            <label className="field" style={{ flex: "1 1 200px" }}>
              <span>Valor{items.some((i) => i.name === name) ? " (vazio = manter o atual)" : ""}</span>
              <div style={{ display: "flex", gap: 4 }}>
                <input
                  type={showValue ? "text" : "password"}
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  autoComplete="new-password"
                  spellCheck={false}
                  style={{ flex: 1, minWidth: 0 }}
                />
                <button type="button" className="btn-page" onClick={() => setShowValue((v) => !v)} aria-label={showValue ? "Ocultar valor" : "Mostrar valor"}>
                  {showValue ? "Ocultar" : "Ver"}
                </button>
              </div>
            </label>
            <label className="field" style={{ flex: "1 1 180px" }}>
              <span>Descrição (o agente lê - diga pra que serve)</span>
              <input
                type="text"
                value={description}
                placeholder="PAT do Azure DevOps (org moc) pra tasks/PRs/board"
                onChange={(e) => setDescription(e.target.value)}
              />
            </label>
            <label className="field" style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 6, width: "auto" }}>
              <input type="checkbox" checked={secret} onChange={(e) => setSecret(e.target.checked)} />
              <span>Secreta (nunca revelar na UI)</span>
            </label>
            <button className="btn-approve" type="button" disabled={busy || !name} onClick={() => void save()}>
              Salvar variável
            </button>
          </div>
        </div>
      )}
      {toDelete && (
        <ConfirmModal
          title="Remover variável"
          message={`Remover a variável "${toDelete}"? O agente deixa de ter acesso a ela na próxima resposta.`}
          confirmLabel="Remover"
          onConfirm={() => void remove(toDelete)}
          onClose={() => setToDelete(null)}
        />
      )}
    </div>
  );
}

/**
 * Atalho usado no card de repositórios - mesma LISTA da organização (o wsId
 * não importa mais; mantido na assinatura pra não mexer no chamador).
 */
export function WorkspaceEnvVars({ orgQs = "" }: { wsId?: string; orgQs?: string }) {
  return <EnvVarsManager qs={orgQs} />;
}
